import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useParams } from 'react-router-dom';
import apiClient, { tenantPortalAPI } from '../services/api';
import { DollarSign, Mail, Phone, Copy, CheckCircle } from 'lucide-react';

const ZellePanel = ({ tenantId, months, amount, setMessage, onSuccess, paymentConfig }) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState('');
  const [sending, setSending] = useState(false);

  if (!paymentConfig?.zelle_enabled) {
    return <div className="text-sm text-gray-500">Zelle not configured for this property.</div>;
  }

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleMarkAsSent = async () => {
    if (sending) return;
    setSending(true);
    
    try {
      // Create a payment record with pending_zelle status
      const paymentData = {
        tenant_id: tenantId,
        amount: amount,
        months: months,
        payment_method: 'zelle',
        status: 'pending_zelle',
        payment_date: new Date().toISOString()
      };

      const headers = tenantPortalAPI.getAuthHeaders();
      await apiClient.post('/api/payments', paymentData, { headers });
      
      setMessage('Payment marked as sent! Your landlord will verify and confirm receipt.');
      setShowModal(false);
      onSuccess();
    } catch (error) {
      console.error('Error recording Zelle payment:', error);
      setMessage('Error recording payment. Please contact your landlord.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button 
        className="w-full bg-purple-600 hover:bg-purple-700"
        onClick={() => setShowModal(true)}
        disabled={!amount || months.length === 0}
      >
        <DollarSign className="h-4 w-4 mr-2" />
        Pay with Zelle (No Fees)
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-purple-600" />
                Pay with Zelle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Amount */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <div className="text-sm text-purple-700 mb-1">Payment Amount</div>
                <div className="text-3xl font-bold text-purple-900">${amount.toFixed(2)}</div>
                <div className="text-xs text-purple-600 mt-1">
                  {months.length} month{months.length > 1 ? 's' : ''} rent payment
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-3">
                <div className="font-medium text-gray-900">Follow these steps:</div>
                <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
                  <li>Open your bank's mobile app or online banking</li>
                  <li>Go to Zelle or "Send Money"</li>
                  <li>Send <strong>${amount.toFixed(2)}</strong> to your landlord using the information below:</li>
                </ol>
              </div>

              {/* Landlord Zelle Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="font-medium text-gray-900">Send Payment To:</div>
                
                {paymentConfig.zelle_email && (
                  <div className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="font-mono text-sm">{paymentConfig.zelle_email}</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(paymentConfig.zelle_email, 'email')}
                    >
                      {copied === 'email' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                )}

                {paymentConfig.zelle_phone && (
                  <div className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="font-mono text-sm">{paymentConfig.zelle_phone}</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(paymentConfig.zelle_phone, 'phone')}
                    >
                      {copied === 'phone' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ <strong>Important:</strong> After sending payment via Zelle, click "I've Sent Payment" below. Your landlord will verify and confirm receipt.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleMarkAsSent}
                  disabled={sending}
                >
                  {sending ? 'Recording...' : "I've Sent Payment"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  disabled={sending}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

const StripePanel = ({ tenantId, months, amount, setMessage, onSuccess, paymentConfig }) => {
  const [stripePk, setStripePk] = useState('');
  const stripePromise = useMemo(() => stripePk ? loadStripe(stripePk) : null, [stripePk]);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);

  // Set Stripe publishable key from payment config
  useEffect(() => {
    if (paymentConfig?.stripe_enabled && paymentConfig?.stripe_publishable_key) {
      setStripePk(paymentConfig.stripe_publishable_key);
    } else if (paymentConfig?.stripe_enabled) {
      // Fallback to environment variable if landlord hasn't configured their own
      setStripePk(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
    }
  }, [paymentConfig]);

  useEffect(() => {
    const createIntent = async () => {
      if (!tenantId || months.length === 0 || !amount || !stripePk) return;
      setLoading(true);
      try {
        const data = await tenantPortalAPI.createStripeIntent({ tenant_id: tenantId, months, total_amount: amount });
        setClientSecret(data.client_secret);
      } catch (e) {
        setMessage(e.response?.data?.detail || e.message || 'Failed to initialize Stripe');
      } finally {
        setLoading(false);
      }
    }
    createIntent();
  }, [tenantId, months, amount, stripePk, setMessage]);

  if (!paymentConfig?.stripe_enabled) return <div className="text-sm text-gray-500">Stripe not configured for this property.</div>;
  if (!stripePk) return <div className="text-sm text-gray-500">Loading Stripe configuration...</div>;
  if (!clientSecret) return loading ? <div>Loading payment...</div> : null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripeCheckoutForm onSuccess={onSuccess} setMessage={setMessage} />
    </Elements>
  );
};

const StripeCheckoutForm = ({ setMessage, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        setMessage(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
        setMessage('Payment succeeded');
      } else {
        setMessage('Payment requires additional action');
      }
    } catch (e) {
      setMessage(e.message || 'Payment error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <Button type="submit" disabled={submitting}>{submitting ? 'Processing...' : 'Pay with Card'}</Button>
    </form>
  );
};

const PayPalPanel = ({ tenantId, months, amount, setMessage, onSuccess, paymentConfig }) => {
  const paypalClientId = paymentConfig?.paypal_client_id || process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const initialOptions = useMemo(() => ({
    'client-id': paypalClientId || 'test',
    currency: 'USD',
    components: 'buttons'
  }), [paypalClientId]);

  if (!paymentConfig?.paypal_enabled && !paypalClientId) {
    return <div className="text-sm text-gray-500">PayPal not configured for this property.</div>;
  }

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PayPalButtons
        style={{ layout: 'vertical' }}
        createOrder={async () => {
          try {
            const order = await tenantPortalAPI.paypalCreateOrder({ tenant_id: tenantId, amount, currency: 'USD', months });
            return order.id;
          } catch (e) {
            setMessage(e.message || 'Failed to create PayPal order');
            throw e;
          }
        }}
        onApprove={async (data) => {
          try {
            await tenantPortalAPI.paypalCapture({ order_id: data.orderID });
            onSuccess();
            setMessage('PayPal payment captured');
          } catch (e) {
            setMessage(e.message || 'Failed to capture PayPal payment');
          }
        }}
        onError={(err) => setMessage(err?.message || 'PayPal error')}
      />
    </PayPalScriptProvider>
  );
};

const TenantPayment = () => {
  const { orgId } = useParams();
  const [tenantId, setTenantId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [authedTenant, setAuthedTenant] = useState('');
  const [paymentConfig, setPaymentConfig] = useState({
    stripe_enabled: false,
    stripe_publishable_key: '',
    paypal_enabled: false,
    paypal_client_id: '',
    use_platform_default: true
  });
  const [months, setMonths] = useState([]);
  const [partial, setPartial] = useState('');
  const [rent, setRent] = useState(1200);
  const [message, setMessage] = useState('');

  // Load payment configuration when tenant is authenticated
  useEffect(() => {
    const loadPaymentConfig = async () => {
      if (!authedTenant) return;
      
      try {
        const headers = tenantPortalAPI.getAuthHeaders();
        const response = await apiClient.get('/api/tenant/payment-config', { headers });
        setPaymentConfig(response.data);
      } catch (error) {
        console.error('Error loading payment config:', error);
        setMessage('Error loading payment options');
      }
    };

    loadPaymentConfig();
  }, [authedTenant]);

  // Check for existing authentication on component load
  useEffect(() => {
    const existingTenantId = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
    const existingToken = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    
    if (existingTenantId && existingToken) {
      setAuthedTenant(existingTenantId);
      setTenantId(existingTenantId);
      
      // Fetch tenant rent amount
      const fetchRent = async () => {
        try {
          // Use tenant-specific endpoint instead of landlord-protected /api/tenants
          const headers = tenantPortalAPI.getAuthHeaders();
          const profile = await apiClient.get('/api/tenant/profile', { headers });
          if (profile.data && profile.data.rent_amount) {
            setRent(profile.data.rent_amount);
          }
        } catch (error) {
          console.error('Error fetching tenant rent amount:', error);
        }
      };
      fetchRent();
    }
  }, []);

  const total = useMemo(() => {
    const base = months.length * rent;
    const p = parseFloat(partial || '0');
    if (partial) return Math.min(p, base - 0.01);
    return base;
  }, [months, rent, partial]);

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const toggleMonth = (idx) => {
    setMonths((prev) => prev.includes(idx+1) ? prev.filter(m => m!==idx+1) : [...prev, idx+1]);
  };

  const login = async () => {
    setMessage('');
    try {
      await tenantPortalAPI.login({ tenant_id: tenantId, access_code: accessCode });
      setAuthedTenant(tenantId);
      
      // Fetch tenant information to get the actual rent amount
      try {
        // Use tenant-specific endpoint instead of landlord-protected /api/tenants
        const headers = tenantPortalAPI.getAuthHeaders();
        const profile = await apiClient.get('/api/tenant/profile', { headers });
        if (profile.data && profile.data.rent_amount) {
          setRent(profile.data.rent_amount);
        }
      } catch (error) {
        console.warn('Could not fetch tenant rent amount:', error);
        // Keep default rent if fetch fails
      }
    } catch (e) {
      setMessage(e.message || 'Login failed');
    }
  };

  const logout = async () => {
    try { await tenantPortalAPI.revoke(); } catch {}
    localStorage.removeItem('tenant_token');
    sessionStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_id');
    setAuthedTenant('');
    setTenantId('');
    setAccessCode('');
    setMessage('Signed out successfully');
    // Clear payment configuration
    setPaymentConfig({
      stripe_enabled: false,
      stripe_publishable_key: '',
      paypal_enabled: false,
      paypal_client_id: '',
      zelle_enabled: false,
      zelle_email: '',
      zelle_phone: '',
      use_platform_default: true
    });
  };

  const success = () => {
    // Reset selection on success
    setMonths([]);
    setPartial('');
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>Tenant Portal Login</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Tenant ID" value={tenantId} onChange={(e)=>setTenantId(e.target.value)} />
            <Input placeholder="Access Code" value={accessCode} onChange={(e)=>setAccessCode(e.target.value)} />
            <Button onClick={login}>Access</Button>
          </div>
          {message && <div className="text-sm text-red-600">{message}</div>}
        </CardContent>
      </Card>

      {authedTenant && (
        <Card>
          <CardHeader>
            <CardTitle>Make a Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="mb-2 text-sm text-gray-600">Select Months</div>
              <div className="grid grid-cols-6 gap-2">
                {monthNames.map((m, idx) => (
                  <button key={m} className={`border rounded px-2 py-1 text-sm ${months.includes(idx+1)?'bg-blue-600 text-white':'bg-white'}`} onClick={()=>toggleMonth(idx)}>{m}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <div className="text-xs text-gray-500">Monthly Rent</div>
                <Input type="number" value={rent} onChange={(e)=>setRent(parseFloat(e.target.value||'0'))} />
              </div>
              <div>
                <div className="text-xs text-gray-500">Partial Payment (optional)</div>
                <Input type="number" value={partial} onChange={(e)=>setPartial(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-500">Total</div>
                <Input disabled value={total ? total.toFixed(2) : ''} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="font-medium mb-2">Pay with Card (Stripe)</div>
                <StripePanel tenantId={authedTenant} months={months} amount={total} setMessage={setMessage} onSuccess={success} paymentConfig={paymentConfig} />
              </div>
              <div>
                <div className="font-medium mb-2">Pay with PayPal</div>
                <PayPalPanel tenantId={authedTenant} months={months} amount={total} setMessage={setMessage} onSuccess={success} paymentConfig={paymentConfig} />
              </div>
              <div>
                <div className="font-medium mb-2">Pay with Zelle</div>
                <ZellePanel tenantId={authedTenant} months={months} amount={total} setMessage={setMessage} onSuccess={success} paymentConfig={paymentConfig} />
              </div>
            </div>

            {message && <div className="text-sm text-gray-700">{message}</div>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenantPayment;